import { useState, useEffect, useCallback, useRef } from 'react';
import {
  PortExtended,
  PortExtendedInnerInner,
  Geometry as ApiGeometry,
} from '@ais-ui/api-spec/portserver';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { usePortAdminGet, usePortUpdate } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';
import {
  useDrawingContext,
  useResetDrawingContext,
  useOpenLayersContext,
  useDrawingLifecycle,
  DrawingStyles,
  zoomToShapeExtent,
} from '@ais-ui/react-openlayers';
import {
  DialogForm,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  LoadingSpinner,
  useToast,
} from '@ais-ui/ui-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useDrawWithViewMode } from '../../../hooks/useDrawWithViewMode';
import { DrawPointField } from '../../Shapes/DrawFormFields';
import { DrawPolygonField } from '../../Shapes/DrawFormFields';
import { InnerAreasList } from '../InnerAreasList';
import { innerAreaFormSchema, convertPolygonToForm } from '../constants';
import { Geometry, pointSchema, polygonSchema } from '@ais-ui/utils';
import { useMultiShapeDrawing, DrawingShape } from '@ais-ui/react-openlayers';
import { useViewMode, ViewMode } from '../../../components/ViewModeProvider';
import { useReplayContext } from '../../Replay/ReplayContext';

interface EditPortDialogProps {
  portId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const editPortFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  locode: z
    .string()
    .min(5, 'Locode must be 5 characters (e.g., DKAAR)')
    .max(5, 'Locode must be 5 characters')
    .regex(
      /^[A-Z]{2}[A-Z0-9]{3}$/,
      'Locode must be 2 letters + 3 alphanumeric (e.g., DKAAR)',
    ),
  country: z.string().min(1, 'Country is required'),
  position: pointSchema.nullable(),
  geometry: polygonSchema.nullable(),
});

type EditPortFormValues = z.infer<typeof editPortFormSchema>;

// Helper function to convert API Point geometry to form geometry
function convertPointGeometry(
  position: ApiGeometry | undefined,
): z.infer<typeof pointSchema> | null {
  if (!position?.coordinates) return null;
  const coords = position.coordinates;
  if (
    !Array.isArray(coords) ||
    coords.length < 2 ||
    coords[0] == null ||
    coords[1] == null
  )
    return null;
  return {
    type: 'Point' as const,
    coordinates: coords as [number, number] | [number, number, number],
  };
}

export const EditPortDialog = ({
  portId,
  open,
  onOpenChange,
}: EditPortDialogProps) => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { data: port, isLoading: isLoadingPort } = usePortAdminGet(portId);
  const updatePort = usePortUpdate();
  const { showDrawPanel, onAccept, onCancel, onFinish, setShowDrawPanel } =
    useDrawingContext();
  const resetDrawingContext = useResetDrawingContext();
  const { onStartDrawing } = useDrawingLifecycle();
  const { displayProjection, dataProjection, map } = useOpenLayersContext();
  const multiShapeDrawing = useMultiShapeDrawing();
  const multiShapeRef = useRef(multiShapeDrawing);
  multiShapeRef.current = multiShapeDrawing;
  const { setViewMode, viewMode } = useViewMode();
  const { resetReplay } = useReplayContext();
  const [showDialog, setShowDialog] = useState(open);
  const [innerAreas, setInnerAreas] = useState<PortExtendedInnerInner[]>([]);

  // Inner area editing state - lifted here to persist across dialog hide/show
  const [innerAreaEditingIndex, setInnerAreaEditingIndex] = useState<
    number | null
  >(null);
  const [isAddingInnerArea, setIsAddingInnerArea] = useState(false);
  // Track shape ID for form-initiated new inner areas (so we populate the form, not innerAreas)
  const formNewAreaIdRef = useRef<string | null>(null);

  const form = useForm<EditPortFormValues>({
    resolver: zodResolver(editPortFormSchema),
    defaultValues: {
      name: '',
      locode: '',
      country: '',
      position: null,
      geometry: null,
    },
  });

  // Inner area form - lives at parent level to persist across dialog hide/show
  const innerAreaForm = useForm<z.infer<typeof innerAreaFormSchema>>({
    resolver: zodResolver(innerAreaFormSchema),
    defaultValues: {
      name: '',
      terminalType: 'Undefined',
      polygon: null,
    },
  });

  const position = useWatch({
    control: form.control,
    name: 'position',
  }) as Geometry | null;
  const geometry = useWatch({
    control: form.control,
    name: 'geometry',
  }) as Geometry | null;

  // Keep point drawing with the standard flow
  const { onStartDrawingFromDialog: onStartDrawingPoint } = useDrawWithViewMode(
    form,
    setShowDialog,
    'position',
  );

  // Return to Normal mode when drawing ends
  useEffect(() => {
    if (!showDrawPanel && viewMode === ViewMode.Drawing) {
      setViewMode(ViewMode.Normal);
    }
  }, [showDrawPanel, viewMode, setViewMode]);

  // Build DrawingShape[] from current form/state
  const buildPortShapes = useCallback((): DrawingShape[] => {
    const shapes: DrawingShape[] = [];

    // Outer boundary
    shapes.push({
      id: 'outer',
      label: 'Port Boundary',
      color: 'blue',
      transparency: 0.85,
      drawingStyle: DrawingStyles.Normal,
      geometry: geometry,
    });

    // Inner areas
    innerAreas.forEach((area, idx) => {
      shapes.push({
        id: `inner-${area.id || idx}`,
        label: area.name || `Inner Area ${idx + 1}`,
        color: 'orange',
        transparency: 0.6,
        drawingStyle: DrawingStyles.InnerArea,
        geometry: convertPolygonToForm(area.polygon),
      });
    });

    return shapes;
  }, [geometry, innerAreas]);

  // Apply shapes from multi-shape context back to form/state
  const applyShapesFromContext = useCallback(
    (shapes: DrawingShape[]) => {
      const outerShape = shapes.find((s) => s.id === 'outer');
      if (outerShape?.geometry) {
        form.setValue(
          'geometry',
          outerShape.geometry as z.infer<typeof polygonSchema>,
          { shouldDirty: true },
        );
      }

      // Build updated inner areas
      const updatedInnerAreas = [...innerAreas];
      const newInnerAreas: PortExtendedInnerInner[] = [];

      shapes.forEach((s) => {
        if (!s.id.startsWith('inner')) return;

        if (s.id.startsWith('inner-new-')) {
          // Check if this was a form-initiated new area — populate the form instead
          if (formNewAreaIdRef.current === s.id) {
            if (s.geometry) {
              innerAreaForm.setValue(
                'polygon',
                {
                  type: 'Polygon' as const,
                  coordinates: s.geometry.coordinates,
                } as z.infer<typeof polygonSchema>,
                { shouldDirty: true },
              );
            }
            formNewAreaIdRef.current = null;
          } else if (s.geometry) {
            // Drawing-menu-initiated new area — add directly
            newInnerAreas.push({
              name:
                s.label === 'New Inner Area'
                  ? `Inner Area ${innerAreas.length + newInnerAreas.length + 1}`
                  : s.label,
              terminalType: 'Undefined',
              polygon: {
                type: 'Polygon',
                coordinates: s.geometry.coordinates as number[][][],
              },
            });
          }
          // Silently discard new areas without geometry
        } else {
          // Existing inner area — extract index from id
          const idPart = s.id.replace('inner-', '');
          const idx = innerAreas.findIndex(
            (a, i) => String(a.id || i) === idPart,
          );
          if (idx >= 0 && s.geometry) {
            updatedInnerAreas[idx] = {
              ...updatedInnerAreas[idx],
              polygon: {
                type: 'Polygon',
                coordinates: s.geometry.coordinates as number[][][],
              },
            };
          }
        }
      });

      setInnerAreas([...updatedInnerAreas, ...newInnerAreas]);
    },
    [form, innerAreas, innerAreaForm],
  );

  // Start multi-shape drawing mode
  const startPortAdminDrawing = useCallback(
    (activeShapeId: string, existingShape?: GeoJson | null) => {
      if (!multiShapeRef.current) return;

      const shapes = buildPortShapes();

      // If the active shape doesn't exist yet (new inner area from form), add it
      if (
        !shapes.find((s) => s.id === activeShapeId) &&
        activeShapeId.startsWith('inner-new-')
      ) {
        shapes.push({
          id: activeShapeId,
          label: 'New Inner Area',
          color: 'orange',
          transparency: 0.6,
          drawingStyle: DrawingStyles.InnerArea,
          geometry: null,
        });
      }

      // Stop replay if active
      if (viewMode === ViewMode.Replay) {
        resetReplay({ resetDate: true });
      }

      // Set up accept callback — called when user clicks Finish
      const handleAccept = () => {
        const allShapes = multiShapeRef.current!.deactivate();
        applyShapesFromContext(allShapes);
        resetDrawingContext();
        onAccept.current = null;
        onFinish.current = null;
        onCancel.current = null;
        setShowDrawPanel(false);
        setViewMode(ViewMode.Normal);
        setShowDialog(true);
      };

      // Set up cancel callback
      const handleCancel = () => {
        multiShapeRef.current!.deactivate();
        resetDrawingContext();
        onAccept.current = null;
        onFinish.current = null;
        onCancel.current = null;
        setViewMode(ViewMode.Normal);
        setShowDialog(true);
      };

      // Set callbacks BEFORE onStartDrawing (which only sets defaults if null)
      onAccept.current = handleAccept;
      onCancel.current = handleCancel;

      // Determine active shape and its drawing style
      const activeShape = shapes.find((s) => s.id === activeShapeId);
      const drawingStyle = activeShape?.drawingStyle ?? DrawingStyles.Normal;

      // Switch to drawing mode
      setViewMode(ViewMode.Drawing);

      // Start the drawing context with the active shape
      onStartDrawing('Polygon', existingShape, true, drawingStyle);

      // Activate multi-shape mode (adds reference layers for non-active shapes)
      multiShapeRef.current.activate(shapes, activeShapeId);

      // Hide dialog
      setShowDialog(false);

      // Zoom to outer polygon for context
      if (geometry?.type === 'Polygon' && geometry.coordinates && map) {
        const outerGeoJson: GeoJson = {
          type: 'Feature',
          properties: { id: 'zoom-target' },
          geometry: {
            type: 'Polygon',
            coordinates: geometry.coordinates as number[][][],
          },
        };
        zoomToShapeExtent(outerGeoJson, map, displayProjection, dataProjection);
      }
    },
    [
      buildPortShapes,
      viewMode,
      resetReplay,
      onAccept,
      onCancel,
      onFinish,
      setShowDrawPanel,
      setViewMode,
      resetDrawingContext,
      applyShapesFromContext,
      onStartDrawing,
      geometry,
      map,
      displayProjection,
      dataProjection,
    ],
  );

  // Outer polygon: start drawing
  const onStartDrawingPolygon = useCallback(
    (_type: string, existingShape?: GeoJson | null) => {
      startPortAdminDrawing('outer', existingShape);
    },
    [startPortAdminDrawing],
  );

  // Inner area: start drawing
  const onStartDrawingInnerArea = useCallback(
    (_type: string, existingShape: GeoJson | null) => {
      // Determine which inner area to activate
      let activeId: string;
      if (innerAreaEditingIndex !== null) {
        const area = innerAreas[innerAreaEditingIndex];
        activeId = `inner-${area.id || innerAreaEditingIndex}`;
        formNewAreaIdRef.current = null;
      } else if (isAddingInnerArea) {
        // Form-initiated new area — track ID so we populate the form on finish
        activeId = `inner-new-${Date.now()}`;
        formNewAreaIdRef.current = activeId;
      } else {
        activeId = 'outer';
        formNewAreaIdRef.current = null;
      }
      startPortAdminDrawing(activeId, existingShape);
    },
    [
      startPortAdminDrawing,
      innerAreaEditingIndex,
      isAddingInnerArea,
      innerAreas,
    ],
  );

  // Populate form when port data loads
  useEffect(() => {
    if (port) {
      form.reset({
        name: port.name || '',
        locode: port.locode || '',
        country: port.country || '',
        position: convertPointGeometry(port.position),
        geometry: convertPolygonToForm(port.outer),
      });
      setInnerAreas(port.inner || []);
    }
  }, [port, form]);

  // Sync internal dialog state with external prop
  useEffect(() => {
    setShowDialog(open);
  }, [open]);

  // Sync with external onOpenChange
  useEffect(() => {
    if (!showDrawPanel && !showDialog && open) {
      onOpenChange(false);
    }
  }, [showDialog, showDrawPanel, open, onOpenChange]);

  const handleSubmit = async (values: EditPortFormValues) => {
    // Check for unsaved inner area
    if (isAddingInnerArea || innerAreaEditingIndex !== null) {
      toast({
        title: 'Unsaved Inner Area',
        description:
          'Please save or cancel the inner area you are editing before submitting.',
        variant: 'destructive',
      });
      return;
    }

    if (!values.geometry) {
      toast({
        title: 'Error',
        description: 'Port boundary is required',
        variant: 'destructive',
      });
      return;
    }

    if (!values.position) {
      toast({
        title: 'Error',
        description: 'Port position is required',
        variant: 'destructive',
      });
      return;
    }

    const portData: PortExtended = {
      ...port,
      name: values.name,
      locode: values.locode.toUpperCase(),
      country: values.country,
      position: {
        type: 'Point',
        coordinates: values.position.coordinates,
      },
      outer: {
        type: 'Polygon',
        coordinates: values.geometry.coordinates,
      },
      inner: innerAreas,
    };

    try {
      await updatePort.mutateAsync({ id: portId, portData });
      toast({
        title: 'Success',
        description: `Port "${values.name}" updated successfully`,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update port:', error);
      toast({
        title: 'Error',
        description: 'Failed to update port',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    if (multiShapeRef.current?.isActive) {
      multiShapeRef.current.deactivate();
    }
    resetDrawingContext();
    onOpenChange(false);
  };

  if (isLoadingPort) {
    return (
      <Form {...form}>
        <DialogForm
          showDialog={showDialog}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              handleCancel();
            }
          }}
          size="md"
        >
          <DialogForm.Header>
            <DialogForm.Title>Edit Port</DialogForm.Title>
            <DialogForm.Description>
              Update port information, position, and boundary.
            </DialogForm.Description>
          </DialogForm.Header>
          <DialogForm.Body isLoading hasLoaded={false} />
        </DialogForm>
      </Form>
    );
  }

  return (
    <Form {...form}>
      <DialogForm
        showDialog={showDialog}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            handleCancel();
          }
        }}
        size="md"
      >
        <DialogForm.Header>
          <DialogForm.Title>Edit Port</DialogForm.Title>
          <DialogForm.Description>
            Update port information, position, and boundary.
          </DialogForm.Description>
        </DialogForm.Header>
        <DialogForm.Body>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Port of Aarhus" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="locode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>UN/LOCODE</FormLabel>
                <FormControl>
                  <Input
                    placeholder="DKAAR"
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value.toUpperCase())
                    }
                    maxLength={5}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Country</FormLabel>
                <FormControl>
                  <Input placeholder="Denmark" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Port Position and Boundary - side by side */}
          <div className="grid grid-cols-2 gap-4">
            {/* Port Position Drawing */}
            <div className="space-y-2">
              <Label>Port Position (Point)</Label>
              <DrawPointField
                form={form}
                geometry={position}
                onStartDrawing={onStartDrawingPoint}
                color="#ef4444"
              />
            </div>

            {/* Port Boundary Drawing */}
            <div className="space-y-2">
              <Label>Port Boundary (Polygon)</Label>
              <DrawPolygonField
                form={form}
                geometry={geometry}
                onStartDrawing={onStartDrawingPolygon}
                color="#3b82f6"
              />
            </div>
          </div>

          {/* Inner Areas */}
          <InnerAreasList
            innerAreas={innerAreas}
            outerPolygon={
              geometry?.type === 'Polygon'
                ? {
                    type: 'Polygon',
                    coordinates: geometry.coordinates as number[][][],
                  }
                : undefined
            }
            onChange={setInnerAreas}
            setShowDialog={setShowDialog}
            editingIndex={innerAreaEditingIndex}
            setEditingIndex={setInnerAreaEditingIndex}
            isAdding={isAddingInnerArea}
            setIsAdding={setIsAddingInnerArea}
            innerAreaForm={innerAreaForm}
            onStartDrawingInnerArea={onStartDrawingInnerArea}
          />
        </DialogForm.Body>
        <DialogForm.Footer>
          <DialogForm.Cancel>{messages.genericCancel}</DialogForm.Cancel>
          <DialogForm.Submit
            disabled={updatePort.isPending}
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            {updatePort.isPending ? (
              <LoadingSpinner size="small" />
            ) : (
              messages.genericSubmit
            )}
          </DialogForm.Submit>
        </DialogForm.Footer>
      </DialogForm>
    </Form>
  );
};
